import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency, toUSD } from "@/lib/currency";
import PageHeader from "@/components/PageHeader";
import Card from "@/components/Card";
import CurrencySelect from "@/components/CurrencySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { fmtDate } from "@/lib/format";
import { Plus, Trash2, Mail, Pencil } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", role: "Employee", department: "", salary: 0 };

export default function StaffPage() {
  const { fmtMoney } = useCurrency();
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [currency, setCurrency] = useState("IDR");
  const [confirmId, setConfirmId] = useState(null);

  const load = () => api.get("/staff").then((r) => setStaff(r.data || []));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditItem(null); setForm(EMPTY); setCurrency("IDR"); setOpen(true); };
  const openEdit = (m) => {
    setEditItem(m);
    setForm({ name: m.name, email: m.email || "", role: m.role, department: m.department || "", salary: m.salary });
    setCurrency("IDR"); // display and input values are always in rupiah
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name) return toast.error("Name is required");
    const payload = { ...form, salary: toUSD(+form.salary, currency) };
    if (editItem) {
      await api.put(`/staff/${editItem.id}`, { ...editItem, ...payload });
      toast.success("Staff updated");
    } else {
      await api.post("/staff", payload);
      toast.success("Staff added");
    }
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/staff/${id}`);
    toast.success("Staff member removed");
    load();
  };

  const total = staff.reduce((s, m) => s + (m.salary || 0), 0);
  const isConverting = false;

  return (
    <div data-testid="staff-page" className="max-w-[1400px] mx-auto">
      <PageHeader
        title="Staff"
         subtitle={`${staff.length} team members · ${fmtMoney(total)} monthly payroll`}
        action={
          <Button data-testid="add-staff-btn" onClick={openCreate} className="rounded-full bg-black text-white hover:bg-black/85">
             <Plus className="w-4 h-4 mr-1.5" /> Add team member
          </Button>
        }
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditItem(null); }}>
        <DialogContent className="rounded-3xl">
           <DialogHeader><DialogTitle>{editItem ? "Edit team member" : "New team member"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            {[["name", "Name", "text"], ["email", "Email", "email"], ["role", "Role", "text"], ["department", "Department", "text"]].map(([k, l, t]) => (
              <div key={k} className="grid gap-1.5">
                <Label className="text-[12px]">{l}</Label>
                <Input data-testid={`staff-input-${k}`} type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-xl" />
              </div>
            ))}
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Monthly salary</Label>
              <div className="flex items-center gap-1.5">
                <Input
                  data-testid="staff-input-salary"
                  type="number" step="0.01"
                  value={form.salary}
                  onChange={(e) => setForm({ ...form, salary: e.target.value })}
                  className="rounded-xl font-mono flex-1 min-w-0"
                />
                <CurrencySelect value={currency} onChange={setCurrency} size="sm" />
              </div>
              {isConverting && +form.salary > 0 && (
                <div className="text-[10.5px] text-[#8E8E93]">
                  ≈ {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(toUSD(+form.salary, currency))} USD/mo stored
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-staff-btn" onClick={submit} className="rounded-full">
               {editItem ? "Save changes" : "Add team member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {staff.map((m) => (
            <div key={m.id} data-testid={`staff-${m.id}`} className="p-5 rounded-2xl bg-white border border-black/[0.04] card-hover">
              <div className="flex items-start justify-between">
                <Avatar className="w-14 h-14">
                  <AvatarImage src={m.avatar_url} alt={m.name} />
                  <AvatarFallback>{m.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</AvatarFallback>
                </Avatar>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => openEdit(m)} className="text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => setConfirmId(m.id)} className="text-[#8E8E93] hover:text-[#FF3B30] transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-3 font-display font-semibold text-[15px]">{m.name}</div>
              <div className="text-[12px] text-[#3C3C43]/80">{m.role} · {m.department}</div>
              {m.email && (
                <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-[#8E8E93]">
                  <Mail className="w-3 h-3" /> {m.email}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-black/[0.04] flex items-center justify-between text-[12px]">
                <span className="text-[#8E8E93]">Since {fmtDate(m.hire_date)}</span>
                <span className="font-mono font-semibold">{fmtMoney(m.salary)}/mo</span>
              </div>
            </div>
          ))}
          {staff.length === 0 && <div className="col-span-full py-8 text-center text-[#8E8E93]">No staff yet.</div>}
        </div>
      </Card>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove staff member?</AlertDialogTitle>
            <AlertDialogDescription>This person will be permanently removed from the team roster. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              onClick={() => { remove(confirmId); setConfirmId(null); }}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
